/**
 * Home — H1 (+ owner intro), hero carousel, category strip, Brands / Shops
 * rails, the "Your daily deals" grid and the "Top categories" / "Popular
 * brands" link blocks. Mirrors the mobile home screen
 * (promostork_mobile/app/(tabs)/index.tsx) with web-native layout.
 *
 * Data comes exclusively from the adapter hooks (no direct Apollo here):
 * useFeaturedPromotions(6) · usePopularBrands(10) · useShops(1, 10) ·
 * useTopCategories() (the category strip's query, served from the same cache).
 */
import { RefreshCw, Tag } from 'lucide-react';
import {
  useFeaturedPromotions,
  usePopularBrands,
  useShops,
} from '@/hooks/useAppData';
import { useTopCategories } from '@/hooks/useCategories';
import { useNavCities } from '@/hooks/useNavCities';
import { useCatalogPath, useRouteLocale } from '@/hooks/useRouteLocale';
import { PageHead } from '@/components/seo/PageHead';
import { useTranslate } from '@/i18n/LocaleLanguage';
import { HOME_FEATURED_LIMIT, HOME_RAIL_LIMIT, homePageHead } from '@/seo/pageHeads';
import { HeroCarousel } from '@/components/carousel/HeroCarousel';
import { CategoryRail } from '@/components/category/CategoryRail';
import { SaleCalendarSection } from '@/components/sale/SaleCalendarSection';
import { HomeIntro } from '@/components/home/HomeIntro';
import { HomeLinkBlocks } from '@/components/home/HomeLinkBlocks';
import { BrandCard, PromotionCard, ShopCard } from '@/components/cards';
import {
  Button,
  CardSkeleton,
  EmptyState,
  RailSkeleton,
  SectionHeader,
} from '@/components/ui';

/** Compact inline error row for a home section (rails fail quietly, not loudly). */
function InlineRetryRow({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-surface px-4 py-3">
      <p className="text-sm text-ink-secondary">{message}</p>
      <Button
        variant="ghost"
        size="sm"
        leftIcon={<RefreshCw size={14} aria-hidden="true" />}
        onClick={onRetry}
      >
        Retry
      </Button>
    </div>
  );
}

export default function HomePage() {
  const featured = useFeaturedPromotions(HOME_FEATURED_LIMIT);
  const brands = usePopularBrands(HOME_RAIL_LIMIT);
  const shops = useShops(1, HOME_RAIL_LIMIT);
  const categories = useTopCategories();
  const navCities = useNavCities();
  // "See all" links: `/pk/en/…` once the URL move is live (web plan W2.1).
  const catalog = useCatalogPath();
  const locale = useRouteLocale();

  // Title, description and H1 from the home formula (web plan W0.6). "Today"
  // only while a promotion this page lists is live; until then (and while
  // loading) it equals index.html's static head. The SEO server builds the
  // same head from the same query (web plan W1.3), and the same catalogue
  // (the route's `t`, web plan W3.0).
  const t = useTranslate();
  const head = homePageHead(featured.items, { t, locale });

  // cache-and-network keeps `loading` true during refetches — only show
  // skeletons when there is nothing cached to render yet.
  const featuredPending = featured.loading && featured.items.length === 0;
  const brandsPending = brands.loading && brands.items.length === 0;
  const shopsPending = shops.loading && shops.items.length === 0;

  const featuredFailed = !!featured.error && featured.items.length === 0;
  const brandsFailed = !!brands.error && brands.items.length === 0;
  const shopsFailed = !!shops.error && shops.items.length === 0;

  // Hide the Brands / Shops sections entirely when they loaded fine but the
  // catalog is empty (no header pointing at an empty rail).
  const hideBrands = !brandsPending && !brandsFailed && brands.items.length === 0;
  const hideShops = !shopsPending && !shopsFailed && shops.items.length === 0;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:py-8">
      <PageHead head={head} />
      {/* The page's one H1 (+ the owner's intro once it exists). Rendered in
          every state, so the heading never waits for data. */}
      <div className="mt-4">
        <h1 className="text-2xl font-extrabold tracking-tight text-ink md:text-3xl">{head.h1}</h1>
        <HomeIntro />
      </div>

      {/* Hero — featured promotions carousel. No fade-in: slide 0's image is
          the LCP element, and an opacity 0 → 1 animation delayed it (W0.12). */}
      <div className="mt-4">
        <HeroCarousel promotions={featured.items} loading={featuredPending} />
      </div>

      {/* Category strip (pinned Favorites + top categories) */}
      <div className="mt-6 animate-fade-up" style={{ animationDelay: '60ms' }}>
        <CategoryRail />
      </div>

      {/* Sale calendar: the next sale event hubs + a link to all dates (W0.15). */}
      <SaleCalendarSection className="mt-10" locale={locale} />

      {/* Brands rail */}
      {!hideBrands && (
        <section
          aria-label="Brands"
          className="mt-10 animate-fade-up"
          style={{ animationDelay: '120ms' }}
        >
          <SectionHeader title="Brands" to={catalog('/brands')} />
          <div className="mt-4">
            {brandsPending ? (
              <RailSkeleton count={6} />
            ) : brandsFailed ? (
              <InlineRetryRow
                message="Couldn't load brands."
                onRetry={brands.refetch}
              />
            ) : (
              <div className="flex gap-4 overflow-x-auto scrollbar-none pb-2">
                {brands.items.map((brand) => (
                  <BrandCard key={brand.id} brand={brand} compact />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Shops rail */}
      {!hideShops && (
        <section
          aria-label="Shops"
          className="mt-10 animate-fade-up"
          style={{ animationDelay: '180ms' }}
        >
          <SectionHeader title="Shops" to={catalog('/shops')} />
          <div className="mt-4">
            {shopsPending ? (
              <RailSkeleton count={6} />
            ) : shopsFailed ? (
              <InlineRetryRow
                message="Couldn't load shops."
                onRetry={shops.refetch}
              />
            ) : (
              <div className="flex gap-4 overflow-x-auto scrollbar-none pb-2">
                {shops.items.map((shop) => (
                  <ShopCard key={shop.id} shop={shop} compact />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Your daily deals — same featured data, grid layout */}
      <section
        aria-label="Your daily deals"
        className="mt-10 animate-fade-up"
        style={{ animationDelay: '240ms' }}
      >
        <SectionHeader title="Your daily deals" to={catalog('/promotions')} />
        <div className="mt-4">
          {featuredPending ? (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <CardSkeleton key={i} />
              ))}
            </div>
          ) : featuredFailed ? (
            <InlineRetryRow
              message="Couldn't load promotions."
              onRetry={featured.refetch}
            />
          ) : featured.items.length === 0 ? (
            <EmptyState
              icon={Tag}
              title="No promotions yet"
              message="Check back soon for amazing deals."
            />
          ) : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
              {featured.items.map((promotion) => (
                <PromotionCard
                  key={promotion.id}
                  promotion={promotion}
                  variant="grid"
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Crawlable directory: plain links built from the data above. */}
      <HomeLinkBlocks categories={categories.items} brands={brands.items} cities={navCities.items} />
    </div>
  );
}
